import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import ReactGA from "react-ga";
import { sendPageView } from "lib/googleAnalytics/page";
import log from "loglevel";
import { Container, Row, Col, Card, Button } from "react-bootstrap";
import Gravatar from "react-gravatar";
import ClipLoader from "react-spinners/ClipLoader";
import { override } from "components/spinner/spinnerOverride";
import { Breadcrumb, BreadcrumbData, Form } from "@avlino-inc/alenza-react";
import {
  decodeJwtAction,
  getCurrentUserAction,
} from "store/actions/actionCreators";
import {
  getCurrentUserReducerResponse,
  getJwtReducerResponse,
  getCurrentUserChangePasswordReducerResponse,
} from "store/selectors";
import {
  CurrentUser,
  CurrentUserChangePasswordRequestType,
  JWT,
  BasicPostDataDeserializerType,
} from "types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import genStyles from "styles/General.module.scss";
import PasswordStrengthMeter from "components/blocks/PasswordStrengthMeter";
import { changePasswordCurrentUserAction } from "store/actions/actionCreators";
import {
  isNotEmpty,
  validPassword,
  isStringValid,
  getInputValidity,
} from "util/formValidators";
import { passwordRequirements } from "util/reactSnippets";
import { timeConverter } from "util/utilityFuncs";
import MainNav from "components/nav/MainNav";
import * as routes from "routes";
import styles from "./UserSettings.module.scss";
import { getRoleNameFromId } from "util/roles";

type ChangePasswordFormType = {
  oldPassword: string | undefined;
  newPassword: string | undefined;
  confirmPassword: string | undefined;
};

const defaultResetPassword: ChangePasswordFormType = {
  oldPassword: undefined,
  newPassword: undefined,
  confirmPassword: undefined,
};

const UserSettingsPage = ({ authed }: { authed: Promise<boolean> }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();

  const [resetPassword, setResetPassword] = useState<ChangePasswordFormType>(
    defaultResetPassword
  );
  const [isModifyingPasswordFields, setIsModifyingPasswordFields] = useState(
    false
  );
  const [isResettingPassword, setIsResettingPassword] = useState(false);
  const [passwordHasBeenReset, setPasswordHasBeenReset] = useState(false);

  const currentUserData: CurrentUser = useSelector((state) =>
    getCurrentUserReducerResponse(state, "data")
  );
  const currentUserChangePasswordData: BasicPostDataDeserializerType = useSelector(
    (state) => getCurrentUserChangePasswordReducerResponse(state, "data")
  );
  const jwtPayloadData: JWT = useSelector((state) =>
    getJwtReducerResponse(state, "data")
  );

  const onHandleChangePasswordClick = (event: any) => {
    event.preventDefault();
    if (
      resetPassword?.oldPassword &&
      resetPassword?.newPassword &&
      resetPassword?.confirmPassword
    ) {
      log.info("All fields are valid! Resetting Password ...");
      setIsResettingPassword(true);
      const request: CurrentUserChangePasswordRequestType = {
        body: {
          old_password: resetPassword.oldPassword,
          new_password: resetPassword.newPassword,
          confirm_password: resetPassword.confirmPassword,
        },
      };
      log.info("-- Dispatching createPasswordNoAuthAction()...");
      dispatch(changePasswordCurrentUserAction(request));
      setResetPassword(defaultResetPassword);
    } else {
      log.info(
        "One or more fields NOT VALID! Will not reset password. " +
          JSON.stringify(resetPassword)
      );
    }
  };

  const validateChangePassword = (
    passwordForm: ChangePasswordFormType | undefined
  ) => {
    const allFields = [
      passwordForm?.oldPassword,
      passwordForm?.newPassword,
      passwordForm?.confirmPassword,
    ];
    const allFieldsFilled = allFields.every(isNotEmpty);
    return allFieldsFilled;
  };

  const breadcrumbItems = useMemo<BreadcrumbData[]>(
    () => [
      {
        url: routes.DASHBOARD_MAIN,
        label: "Command Center",
      },
      {
        url: routes.SETTINGS,
        label: "Settings",
      },
    ],
    []
  );

  useEffect(() => {
    if (authed) {
      authed.then((allow) => {
        log.info("Allow: " + allow);
        if (allow) {
          log.info("Authenticated. Stay on page...");
        } else {
          log.info("Redirect back...");
          history.push(routes.LOGIN);
        }
      });
    } else {
      history.push(routes.LOGIN);
    }
  }, [authed, history]);

  useEffect(() => {
    log.info("UserSettingsPage useEffect()...");
    dispatch(decodeJwtAction({}));
    dispatch(getCurrentUserAction({}));
  }, [dispatch]);

  useEffect(() => {
    log.info("Password Successfully Changed!");
    setIsResettingPassword(false);
    setIsModifyingPasswordFields(false);
    setResetPassword(defaultResetPassword);
    setPasswordHasBeenReset(true);
  }, [currentUserChangePasswordData]);

  /**
   * On any update to Change Password form field, isModifyingPasswordFields will be true
   */
  useEffect(() => {
    setIsModifyingPasswordFields(true);
  }, [resetPassword]);

  /**
   * Google Analytics
   */
  useEffect(() => {
    sendPageView(location.pathname + location.search);
  }, [location]);
  
  return (
    <>
      <MainNav
        first_name={jwtPayloadData && jwtPayloadData.first_name}
        last_name={jwtPayloadData && jwtPayloadData.last_name}
        username={jwtPayloadData && jwtPayloadData.username}
        email={jwtPayloadData && jwtPayloadData.email}
      />
      <Container fluid={true}>
        <div style={{ margin: "15px" }}>
          <Breadcrumb items={breadcrumbItems} onClickItem={history.push} />
        </div>
        <Row className="h-100 justify-content-md-center">
          <Col
            xs={12}
            md={12}
            lg={9}
            xl={5}
            style={{ justifyContent: "center" }}
          >
            {currentUserData && jwtPayloadData ? (
              <Card id="UserSettingsCard">
                <Card.Body>
                  <Container>
                    <Row>
                      <span style={{ marginRight: "15px" }}>
                        <Gravatar
                          className={styles["gravatar"]}
                          style={{ borderRadius: "50%" }}
                          size={75}
                          default="retro"
                          email={currentUserData.email}
                        />
                      </span>
                      <span>
                        <div id="user-description">
                          <span className={styles["username"]}>
                            {currentUserData.username}
                          </span>
                          <span className={styles["role"]}>
                            {getRoleNameFromId(jwtPayloadData.role_id)}
                          </span>
                          <span style={{ display: "block" }}>
                            Member Since:{" "}
                            {timeConverter(currentUserData.from_dt)}
                          </span>
                          <a
                            href="https://en.gravatar.com/"
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            Change Avatar
                          </a>
                        </div>
                      </span>
                    </Row>
                  </Container>
                  <hr />
                  <div id="profile-general">
                    <h5>
                      General
                      <span>
                        <FontAwesomeIcon
                          title="Contact an Administrator to modify"
                          style={{
                            color: "gray",
                            paddingLeft: "5px",
                          }}
                          icon={["fal", "lock-alt"]}
                        />
                      </span>
                    </h5>
                    <div>
                      <span>Name: </span>
                      <span>{`${currentUserData.first_name} ${currentUserData.last_name}`}</span>
                    </div>
                    <div style={{ marginTop: "5px" }}>
                      <span>Email: </span>
                      <span>{currentUserData.email}</span>
                    </div>
                  </div>
                  <hr />
                  <div id="SettingsPassword">
                    <h5>Password</h5>
                    <form name="change-password-form">
                      <Row>
                        <Col sm={12} md={6} lg={6}>
                          <div className={styles["change-password-form"]}>
                            <Form.Group controlId="reset_current_password">
                              <Form.Input
                                type="password"
                                className={
                                  styles["change-password-form__input"]
                                }
                                placeholder="Current Password"
                                value={resetPassword?.oldPassword || ""}
                                status={
                                  resetPassword?.oldPassword !== "" &&
                                  resetPassword?.oldPassword !== undefined
                                    ? "valid"
                                    : undefined
                                }
                                onChange={(e: any) => {
                                  if (e.target instanceof HTMLInputElement) {
                                    const value = e.target.value;
                                    setResetPassword((state): typeof state => ({
                                      ...state,
                                      oldPassword: value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>

                            <Form.Group controlId="reset_new_password">
                              <Form.Input
                                type="password"
                                className={
                                  styles["change-password-form__input"]
                                }
                                placeholder="New Password"
                                value={resetPassword?.newPassword || ""}
                                status={getInputValidity(
                                  resetPassword?.newPassword,
                                  validPassword
                                )}
                                onChange={(e: any) => {
                                  if (e.target instanceof HTMLInputElement) {
                                    const value = e.target.value;
                                    setResetPassword((state): typeof state => ({
                                      ...state,
                                      newPassword: value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                            <Form.Group controlId="reset_confirm_password">
                              <Form.Input
                                type="password"
                                className={
                                  styles["change-password-form__input"]
                                }
                                placeholder="Confirm Password"
                                value={resetPassword?.confirmPassword || ""}
                                status={
                                  resetPassword?.confirmPassword !== "" &&
                                  resetPassword?.confirmPassword !== undefined
                                    ? "valid"
                                    : undefined
                                }
                                onChange={(e: any) => {
                                  if (e.target instanceof HTMLInputElement) {
                                    const value = e.target.value;
                                    setResetPassword((state): typeof state => ({
                                      ...state,
                                      confirmPassword: value,
                                    }));
                                  }
                                }}
                              />
                            </Form.Group>
                          </div>
                        </Col>
                        <Col sm={12} md={6} lg={6}>
                          {resetPassword.newPassword ? (
                            <PasswordStrengthMeter
                              password={resetPassword.newPassword}
                            />
                          ) : null}
                        </Col>
                      </Row>
                      <Row>
                        <Col md={12} lg={6}>
                          {passwordRequirements()}
                        </Col>
                      </Row>
                      <Button
                        type="submit"
                        className={genStyles["alenza__btn-primary"]}
                        center-block="true"
                        disabled={
                          !validateChangePassword(resetPassword) ||
                          isResettingPassword
                        }
                        onClick={(e: any) => {
                          onHandleChangePasswordClick(e);
                        }}
                      >
                        {isResettingPassword ? (
                          <ClipLoader
                            color={"#ffffff"}
                            css={override}
                            size={14}
                            loading={isResettingPassword}
                          />
                        ) : (
                          <>Change Password</>
                        )}
                      </Button>
                      {passwordHasBeenReset && !isModifyingPasswordFields ? (
                        <p className={styles["password-change-success"]}>
                          Password updated successfully
                        </p>
                      ) : null}
                    </form>
                  </div>
                </Card.Body>
              </Card>
            ) : null}
          </Col>
        </Row>
      </Container>
    </>
  );
};

export default UserSettingsPage;
